import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { showDownloadButton } from "../../../Utils/Function";
import Functions from "../../../Helpers/Functions";
import SessionDescriptionRepository from "../../../Repositories/SessionDescriptionRepository";
import QuoteRepository, { QuoteInfo } from "../../../Repositories/QuoteRepository";
import { isRelationTeachrCourse } from "../../../App/DeclarationV2/components/relationTeachr";
import { getActiveBrandConfig, isDepositEnabled } from "../../../config/environment.config";
import { PunctualCourseDashboard } from "../../../Objects/models/PunctualCourseDashboard";
import { isVerifiedTrainer, vtCoursePrixTotal } from "../../../Utils/VerifiedTrainerAmounts";
import { DV2 } from "../../../App/DeclarationV2/theme";
import { clock, dayLabel, euros, hours as formatHours, initials, plural } from "../../../App/DeclarationV2/format";
import { vocab } from "../../../App/DeclarationV2/vocabulary";
import { Avatar, Button, Card, TextLink, useDv2Theme } from "../../../App/DeclarationV2/layout/Ui";
import { courseDatesLabel, isUndetailedMonthCourse } from "../../../App/CoursesV2/courseRows";

/**
 * Détails d'un cours, fenêtre repensée (maquettes 3a et 3b du 30 octobre
 * 2026). Mêmes données, même statut, mêmes documents que `DetailCours` ;
 * l'ordre de lecture change : statut et argent en haut, séances, déroulement
 * (sans le texte de présentation du profil), documents en une seule liste.
 * Mêmes props que `DetailCours`, pour se brancher aux mêmes endroits.
 *
 * L'en-tête (`DetailCoursV2Header`) remplace le titre « Détails du cours »
 * de la fenêtre : matière et élève, statut avec son explication, résumé.
 */
export interface CoursProps {
    eleve: string;
    matiere: string;
    isCoursePass?: boolean;
    date: string;
    heure: string;
    duree: string;
    remuneration: string;
    prixTotal?: string;
    payeParClient?: string;
    tvaAmount?: string | number | null;
    tvaRate?: string | number | null;
    acompte: string;
    format: string;
    infos?: string;
    sessionDescription?: string | null;
    teachrId?: number | null;
    reste?: string;
    proofDeposit?: string;
    proofDeposits?: string[];
    punctual?: PunctualCourseDashboard;
    displayStatut?: any;
    depositDate?: string | Date | any;
    depositAttestation?: { contentUrl: string; signedAt: string; depositAmount: number; mergedProofCount?: number } | null;
}

const toNumber = (value: unknown): number => {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
};

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/** Titre « Allemand avec Daphi Dock » (Verified Trainer : « Prestation avec … »). */
const courseTitle = (matiere: string, eleve: string): string => {
    if (isVerifiedTrainer()) return `${capitalize(vocab.course())} avec ${eleve}`;
    return `${matiere ? capitalize(matiere) : capitalize(vocab.course())} avec ${eleve}`;
};

/** « Cours ponctuel, 1 h, jeudi 15 octobre 2026 à 07h30 » ou « Plusieurs cours, 2 séances, 3 h au total, du … au … ». */
const courseSummary = ({ punctual, format, duree, date, heure }: CoursProps): string => {
    const course = punctual as any;
    const sessions: Array<{ date: string; durationMinutes: number }> = Array.isArray(course?.sessions) ? course.sessions : [];
    const totalHours = toNumber(course?.duration) / 2;
    const start = course?.startDatetime ? moment(course.startDatetime) : null;
    const end = course?.endDatetime ? moment(course.endDatetime) : null;
    const period = start && end
        ? (start.isSame(end, "day")
            ? dayLabel(start, true).toLowerCase()
            : `du ${dayLabel(start).toLowerCase()} au ${dayLabel(end, true).toLowerCase()}`)
        : date;
    if (isUndetailedMonthCourse(course)) {
        return `${vocab.multipleCoursesTitle()}, séances non détaillées, ${formatHours(totalHours)} au total, ${period}`;
    }
    if (sessions.length > 1) {
        return `${vocab.multipleCoursesTitle()}, ${plural(sessions.length, "séance")}, ${formatHours(totalHours)} au total, ${period}`;
    }
    const time = start ? clock(start) : heure;
    return `${format}, ${duree}, ${period} à ${time}`;
};

/**
 * En-tête de la fenêtre, à passer en `title` du DialogLayout : avatar,
 * « Matière avec Élève », statut (pastille et explication, comme avant),
 * puis le résumé du cours.
 */
export const DetailCoursV2Header: React.FC<CoursProps> = (props) => {
    const { eleve, matiere, isCoursePass, displayStatut } = props;
    const { fontFamily, text } = useDv2Theme();
    return (
        <div className="flex items-start gap-3" style={{ fontFamily, color: text, fontWeight: 400, paddingRight: 8 }}>
            <Avatar text={initials({ childFirstName: eleve.split(" ")[0], childLastName: eleve.split(" ").slice(1).join(" ") })} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{courseTitle(matiere, eleve)}</div>
                <div style={{ marginTop: 6 }}>
                    {!isCoursePass && displayStatut}
                    {isCoursePass && <span style={{ background: DV2.warningBg, color: DV2.warning, fontSize: 12, fontWeight: 800, padding: "4px 10px", borderRadius: 999 }}>Brouillon</span>}
                </div>
                <div style={{ fontSize: 13, color: DV2.muted, marginTop: 6, lineHeight: 1.4 }}>{courseSummary(props)}</div>
            </div>
        </div>
    );
};

/** Ligne de la liste des documents : vignette, titre, détail, action. */
const DocumentRow = ({ kind, title, detail, action }: { kind: "PDF" | "IMG"; title: React.ReactNode; detail: React.ReactNode; action: React.ReactNode }) => {
    const { primary, fontFamily } = useDv2Theme();
    return (
        <div className="flex items-center gap-3" style={{ padding: "12px 0", borderTop: `1px solid ${DV2.borderSoft}`, fontFamily }}>
            <div aria-hidden style={{ width: 40, height: 48, borderRadius: 8, background: DV2.tint, border: "1px solid #C9D9EC", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, color: primary, flexShrink: 0 }}>{kind}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
                <div style={{ fontSize: 12, color: DV2.muted }}>{detail}</div>
            </div>
            <div style={{ flexShrink: 0 }}>{action}</div>
        </div>
    );
};

/** Tuile d'un montant : libellé, valeur, précision. */
const AmountTile = ({ label, value, hint, highlight }: { label: string; value: string; hint?: React.ReactNode; highlight?: boolean }) => {
    const { primary } = useDv2Theme();
    return (
        <Card style={highlight ? { background: primary, border: "none", color: "#fff", padding: "16px 18px" } : { padding: "16px 18px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: highlight ? undefined : DV2.muted, opacity: highlight ? 0.85 : 1 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</div>
            {hint && <div style={{ fontSize: 12, color: highlight ? undefined : DV2.muted, opacity: highlight ? 0.85 : 1, lineHeight: 1.45, marginTop: 2 }}>{hint}</div>}
        </Card>
    );
};

/** Accordéon : titre cliquable avec chevron, contenu replié par défaut. */
const Accordion = ({ id, title, hint, children, defaultOpen = false }: { id: string; title: string; hint?: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean }) => {
    const { fontFamily, text } = useDv2Theme();
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card padding={0} style={{ overflow: "hidden" }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-controls={`${id}-panel`}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 20px", background: "none", border: "none", cursor: "pointer", fontFamily, color: text, textAlign: "left" }}
            >
                <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 800, fontSize: 15 }}>{title}</span>
                    {hint && <span style={{ display: "block", fontSize: 12, color: DV2.muted, marginTop: 2 }}>{hint}</span>}
                </span>
                <span aria-hidden style={{ fontSize: 18, color: DV2.placeholder, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flexShrink: 0 }}>⌄</span>
            </button>
            {open && (
                <div id={`${id}-panel`} style={{ padding: "0 20px 16px", borderTop: `1px solid ${DV2.borderSoft}` }}>
                    <div style={{ paddingTop: 12 }}>{children}</div>
                </div>
            )}
        </Card>
    );
};

const DetailCoursV2: React.FC<CoursProps> = (props) => {
    const {
        isCoursePass, date, heure, duree, remuneration, prixTotal, payeParClient, tvaAmount, tvaRate,
        acompte, format, infos, sessionDescription, teachrId, reste, proofDeposit, proofDeposits, punctual, depositDate, depositAttestation,
    } = props;
    const { fontFamily, text, brandName } = useDv2Theme();
    const navigate = useNavigate();
    const course = punctual as any;
    const isVt = isVerifiedTrainer();
    // relation Teach'r : seule la rémunération du prof est montrée (le prix total comprend la part de la plateforme)
    const showClientAmounts = isVt && !isRelationTeachrCourse(course);

    // Déroulement des séances : celui du cours s'il en a un, sinon la description habituelle du prof.
    const { data: genericDescriptionData } = useQuery({
        queryKey: ["session-description", teachrId],
        queryFn: () => SessionDescriptionRepository.get(teachrId as number),
        enabled: !!teachrId && !sessionDescription,
        staleTime: 5 * 60_000,
    });
    const effectiveDescription = sessionDescription || genericDescriptionData?.sessionDescription?.description || null;

    // Devis du cours (jamais pour un brouillon : le cours n'est pas encore créé).
    const [quote, setQuote] = useState<QuoteInfo | null>(null);
    useEffect(() => {
        if (!course?.id || isCoursePass) return undefined;
        let cancelled = false;
        QuoteRepository.getQuoteForCourse(course.id)
            .then((response) => { if (!cancelled) setQuote(response?.quote ?? null); })
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [course?.id, isCoursePass]);
    const openQuote = () => {
        if (!quote) return;
        QuoteRepository.download(quote.id)
            .then((response) => { if (response?.url) window.open(response.url, "_blank", "noopener"); })
            .catch(() => undefined);
    };

    // Montants enregistrés à la déclaration (jamais recalculés avec le taux courant).
    const totalTtc = prixTotal !== undefined ? toNumber(prixTotal) : vtCoursePrixTotal(course);
    const advance = payeParClient !== undefined ? totalTtc - toNumber(payeParClient) : totalTtc / 2;
    const deposit = isDepositEnabled() ? toNumber(acompte) : 0;
    const remaining = reste !== undefined ? toNumber(reste) : Math.max(totalTtc / 2 - deposit, 0);
    const received = toNumber(remuneration);
    const storedVat = tvaAmount != null && tvaAmount !== "" ? Number(tvaAmount) : null;
    const halfHours = toNumber(course?.duration);
    const totalHours = halfHours / 2;
    const receivedHourly = totalHours > 0 ? received / totalHours : null;
    const depositDateLabel = depositDate instanceof Date
        ? moment(depositDate).locale("fr").format("D MMMM")
        : typeof depositDate === "string" && depositDate ? depositDate : null;
    const receivedHint = `${receivedHourly != null ? `soit ${euros(receivedHourly)} par heure, ` : ""}reversés par ${brandName}${storedVat != null && storedVat > 0 ? `, dont ${euros(storedVat)} de TVA${tvaRate != null && tvaRate !== "" ? ` (${Number(tvaRate)} %)` : ""}` : ""}`;

    const sessions: Array<{ id?: number; date: string; durationMinutes: number }> = Array.isArray(course?.sessions) ? course.sessions : [];
    const sessionsCount = sessions.length;
    const start = course?.startDatetime ? moment(course.startDatetime) : null;
    const end = course?.endDatetime ? moment(course.endDatetime) : null;

    const invoiceNode = showDownloadButton(punctual);
    const helpPath = `/${getActiveBrandConfig()}/tableau-de-bord/aide-contact`;

    return (
        <div style={{ fontFamily, color: text }} className="flex flex-col gap-4">
            {/* Argent : la rémunération du prof ; chez Teach'r et Déclarons, pas de TTC payé par le client
                (comme l'ancienne fiche), l'acompte et le reste à charge seulement s'il y a un acompte. */}
            <div className={`grid grid-cols-1 gap-3 ${showClientAmounts || deposit > 0 ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}>
                <AmountTile label="Vous recevez" value={euros(received)} hint={receivedHint} highlight />
                {showClientAmounts && (
                    <>
                        <AmountTile label="Le client paie" value={euros(totalTtc)} hint={`TTC, dont ${euros(advance, 0)} d'avance immédiate`} />
                        <AmountTile label="Reste à sa charge" value={euros(remaining)} hint={deposit > 0 ? `après un acompte de ${euros(deposit, 0)}${depositDateLabel ? ` le ${depositDateLabel}` : ""}` : "après l'avance immédiate"} />
                    </>
                )}
                {!showClientAmounts && deposit > 0 && (
                    <>
                        <AmountTile label="Acompte versé" value={euros(deposit)} hint={depositDateLabel ? `reçu le ${depositDateLabel}` : `déjà reçu de ${vocab.yourClient()}`} />
                        <AmountTile label="Reste à charge" value={euros(remaining)} hint={`à payer par ${vocab.yourClient()}, avance immédiate déduite`} />
                    </>
                )}
            </div>

            {/* Séances */}
            <Card style={{ padding: "16px 20px" }}>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>{sessionsCount > 1 || isUndetailedMonthCourse(course) ? "Séances" : "Séance"}</div>
                {isUndetailedMonthCourse(course) ? (
                    <div className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 14 }}>{courseDatesLabel(course)}</div>
                            <div style={{ fontSize: 12, color: DV2.muted }}>Séances non détaillées : avec l'ancienne déclaration, les {vocab.courses()} du mois étaient {vocab.declared()}s sur une période globale, sans le détail des jours. Vos prochaines déclarations enregistrent chaque séance, qui s'affichera ici une par une.</div>
                        </div>
                        <div style={{ fontSize: 13, color: DV2.muted, minWidth: 56, textAlign: "right" }}>{duree} au total</div>
                    </div>
                ) : sessionsCount > 0 ? sessions.map((session, index) => {
                    const s = moment(session.date);
                    const e = s.clone().add(session.durationMinutes ?? 0, "minutes");
                    return (
                        <div key={session.id ?? index} className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 800, fontSize: 14 }}>{dayLabel(s)}</div>
                                <div style={{ fontSize: 12, color: DV2.muted }}>Séance {index + 1}</div>
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>{clock(s)} à {clock(e)}</div>
                            <div style={{ fontSize: 13, color: DV2.muted, minWidth: 56, textAlign: "right" }}>{Functions.renderDuration((session.durationMinutes ?? 0) / 30)}</div>
                        </div>
                    );
                }) : (
                    <div className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 14 }}>{start ? dayLabel(start) : date}</div>
                            <div style={{ fontSize: 12, color: DV2.muted }}>{format}</div>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{start && end ? `${clock(start)} à ${clock(end)}` : heure}</div>
                        <div style={{ fontSize: 13, color: DV2.muted, minWidth: 56, textAlign: "right" }}>{duree}</div>
                    </div>
                )}
            </Card>

            {/* Déroulement et note, en accordéons */}
            {effectiveDescription && (
                <Accordion id="dc2-deroulement" title={`Déroulement ${vocab.ofTheCourse()}`} hint={!sessionDescription ? "Votre description habituelle, reprise sur le devis." : `Propre à ${vocab.thisCourse()}.`}>
                    <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.55, whiteSpace: "pre-line" }}>{effectiveDescription}</div>
                </Accordion>
            )}
            {infos && (
                <Accordion id="dc2-note" title={`Note pour ${vocab.theClient()}`}>
                    <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.55 }}>{infos}</div>
                </Accordion>
            )}

            {/* Documents, en une seule liste */}
            {!isCoursePass && (
                <Card style={{ padding: "16px 20px" }}>
                    <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>Documents</div>
                    <DocumentRow
                        kind="PDF"
                        title={quote ? `Devis ${quote.quoteNumber}` : "Devis"}
                        detail={quote ? `${euros(quote.totalAmount)} TTC${quote.status === "active" ? ", en vigueur" : ", remplacé"}` : "Généré à la déclaration, disponible dans quelques instants"}
                        action={quote ? <Button label="Ouvrir" variant="secondary" size="sm" onClick={openQuote} /> : null}
                    />
                    <DocumentRow
                        kind="PDF"
                        title="Facture"
                        detail={invoiceNode ? "Émise après validation du client" : "Émise après validation du client, pas encore disponible"}
                        action={invoiceNode ? <span style={{ fontSize: 13 }}>{invoiceNode}</span> : null}
                    />
                    {isDepositEnabled() && depositAttestation && (
                        <DocumentRow
                            kind="PDF"
                            title="Attestation sur l'honneur"
                            detail={`Acompte de ${euros(depositAttestation.depositAmount, 0)}, signée le ${moment(depositAttestation.signedAt).locale("fr").format("D MMMM YYYY")}${(depositAttestation.mergedProofCount ?? 0) > 0 ? `, ${plural(depositAttestation.mergedProofCount ?? 0, "justificatif")} en annexe` : ""}`}
                            action={<TextLink label="Télécharger" href={depositAttestation.contentUrl} />}
                        />
                    )}
                    {isDepositEnabled() && proofDeposits && proofDeposits.length > 0 && proofDeposits.map((url, index) => (
                        <DocumentRow
                            key={url}
                            kind="IMG"
                            title={proofDeposits.length > 1 ? `Preuve du versement ${index + 1}` : "Preuve du versement"}
                            detail={url.split("/").pop() ?? ""}
                            action={<TextLink label="Télécharger" href={url} />}
                        />
                    ))}
                    {isDepositEnabled() && (!proofDeposits || proofDeposits.length === 0) && proofDeposit && (
                        <DocumentRow kind="IMG" title="Preuve du versement" detail={proofDeposit.split("/").pop() ?? ""} action={<TextLink label="Télécharger" href={proofDeposit} />} />
                    )}
                </Card>
            )}

            <div style={{ fontSize: 13, color: DV2.muted }}>
                Un problème sur {vocab.thisCourse()} ?{" "}
                <TextLink label={`Contacter l'équipe ${brandName}`} onClick={() => navigate(helpPath, { state: { subject: "Problème sur un cours" } })} />
            </div>
        </div>
    );
};

export default DetailCoursV2;
